import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, BOOKING_URL } from "@/config";
import { NEEDS_OPTIONS, BUDGET_OPTIONS, getSource } from "@/components/Contact";

const STEPS = ["Focus", "Budget", "Details", "Goals"];

export default function QualifyPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [qualified, setQualified] = useState(false);
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", phone: "", website: "",
    industry: "", needs: [], budget: "", goals: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleNeed = (n) =>
    setForm({ ...form, needs: form.needs.includes(n) ? form.needs.filter((x) => x !== n) : [...form.needs, n] });

  const canNext =
    step === 0 ? form.needs.length > 0
    : step === 1 ? !!form.budget
    : step === 2 ? form.name.trim() && /.+@.+\..+/.test(form.email)
    : true;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: getSource("qualifying-form") });
      setQualified(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => (step < 3 ? setStep(step + 1) : submit());

  return (
    <main data-testid="qualify-page" className="relative flex min-h-screen items-center overflow-hidden px-6 py-32 md:px-12">
      <div className="absolute -right-40 top-0 h-[500px] w-[500px] bg-crimson/10 blur-[160px]" aria-hidden="true" />
      <div className="relative mx-auto grid w-full max-w-[1400px] gap-16 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <p className="flex items-center gap-3 font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">
            <span className="h-1.5 w-1.5 bg-crimson" /> Strategy Call Qualification
          </p>
          <h1 className="mt-8 font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Let&rsquo;s make sure we&rsquo;re the right weapon for the fight.
          </h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400">
            Answer four quick questions. If we&rsquo;re a fit, you&rsquo;ll get direct access to
            our calendar to lock in your strategy call.
          </p>
          <div className="mt-12 flex gap-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex-1">
                <div className={`h-0.5 transition-colors duration-500 ${i <= step || qualified ? "bg-crimson" : "bg-line"}`} />
                <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.2em] ${i <= step || qualified ? "text-zinc-300" : "text-zinc-600"}`}>{s}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-line bg-surface p-8 md:p-12">
          {qualified ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="qualify-success"
              className="flex min-h-[420px] flex-col items-start justify-center"
            >
              <CalendarCheck size={32} strokeWidth={1.5} className="text-crimson" />
              <h2 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight text-white">
                You&rsquo;re qualified. Lock in your call.
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
                Your details are in our system. Pick a time below — we&rsquo;ll come prepared
                with an initial read on your growth gaps.
              </p>
              <a
                data-testid="qualify-calendly-link"
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-10 inline-block bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white"
              >
                Schedule Your Strategy Call
              </a>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -32 }}
                transition={{ duration: 0.35 }}
              >
                {step === 0 && (
                  <div data-testid="qualify-step-needs">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">01 / What do you need help with?</p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {NEEDS_OPTIONS.map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`qualify-need-${n.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => toggleNeed(n)}
                          aria-pressed={form.needs.includes(n)}
                          className={`border px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider transition-colors duration-300 ${
                            form.needs.includes(n)
                              ? "border-crimson bg-crimson/10 text-white"
                              : "border-line text-zinc-500 hover:border-zinc-400 hover:text-zinc-300"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="qualify-step-budget">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">02 / Monthly marketing &amp; growth budget</p>
                    <div className="mt-6 grid gap-2">
                      {BUDGET_OPTIONS.map((b) => (
                        <button
                          key={b}
                          type="button"
                          data-testid={`qualify-budget-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          onClick={() => setForm({ ...form, budget: b })}
                          aria-pressed={form.budget === b}
                          className={`border px-5 py-4 text-left font-display text-sm font-bold uppercase tracking-wide transition-colors duration-300 ${
                            form.budget === b
                              ? "border-crimson bg-crimson/10 text-white"
                              : "border-line text-zinc-400 hover:border-zinc-400 hover:text-white"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="qualify-step-details">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">03 / Your details</p>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                      <input data-testid="qualify-name-input" placeholder="Name *" value={form.name} onChange={set("name")} className="tpa-input" />
                      <input data-testid="qualify-business-input" placeholder="Business Name" value={form.business_name} onChange={set("business_name")} className="tpa-input" />
                      <input data-testid="qualify-email-input" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className="tpa-input" />
                      <input data-testid="qualify-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className="tpa-input" />
                      <input data-testid="qualify-website-input" placeholder="Website" value={form.website} onChange={set("website")} className="tpa-input" />
                      <input data-testid="qualify-industry-input" placeholder="Industry" value={form.industry} onChange={set("industry")} className="tpa-input" />
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="qualify-step-goals">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">04 / What are you trying to accomplish?</p>
                    <textarea
                      data-testid="qualify-goals-textarea"
                      rows={6}
                      placeholder="Tell us where the business is today — and where you want it to be."
                      value={form.goals}
                      onChange={set("goals")}
                      className="tpa-input mt-6 resize-none"
                    />
                  </div>
                )}

                <div className="mt-10 flex items-center justify-between">
                  <button
                    type="button"
                    data-testid="qualify-back-btn"
                    onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:text-white"
                  >
                    <ArrowLeft size={14} /> {step === 0 ? "Home" : "Back"}
                  </button>
                  <button
                    type="button"
                    data-testid="qualify-next-btn"
                    onClick={next}
                    disabled={!canNext || submitting}
                    className="flex items-center gap-2 bg-white px-7 py-3.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white disabled:opacity-40"
                  >
                    {submitting ? "Submitting…" : step === 3 ? "Submit & Get My Link" : "Next"} <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </main>
  );
}
